import React from 'react';
import PropTypes from 'prop-types';
import { ActionCableConsumer } from 'react-actioncable-provider';
import { connect } from 'react-redux';
import { loadNewState } from '../actions';
import { updateAssembledDocuments } from '../actions/assembledDocuments';
import { updateUi, appendToFastForwardProgress } from '../actions/ui';
import { loadSessionRequest } from '../api';
import {
  getSessionId,
  getHasWebsocketConnection,
} from '../selectors';

class ActionCableListener extends React.Component {
  static propTypes = {
    sessionId: PropTypes.string,
    updateAssembledDocuments: PropTypes.func.isRequired,
    updateUi: PropTypes.func.isRequired,
    appendToFastForwardProgress: PropTypes.func.isRequired,
    loadNewState: PropTypes.func.isRequired,
    hasWebsocketConnection: PropTypes.bool,
  }

  static defaultProps = {
    sessionId: null,
    hasWebsocketConnection: false,
  }

  handleBackgroundAssemblyCompletion = (data) => {
    this.props.updateAssembledDocuments(data);
  }

  handleAppSessionTransmission = (data) => {
    this.props.loadNewState(data);
  }

  handleSnapshotChannelTransmission = (data) => {
    const { message } = data;
    this.props.updateUi({
      isMakingSnapshot: false,
      snapshotStatusMessage: message,
    });
  }

  handleFastForwardProgressChannelTransmission = (data) => {
    this.props.appendToFastForwardProgress(data);
  }

  handleAppSessionConnection = () => {
    // if i did not previously have websocket connection,
    // update has websocket to true
    // load new session request

    // the goal here is to reload the session immediately after the main websocket connection is established,
    // to prevent the situation where document assembly on the first page of the app never appears to complete,
    // because the document is assembled and the server sends the update signal BEFORE websocket connection is established
    if (!this.props.hasWebsocketConnection) {
      const success = (object) => {
        this.props.updateUi({ hasWebsocketConnection: true }, () => { this.props.loadNewState(object); });
      };
      const failure = (message) => {
        alert(message);
      };
      const { sessionId } = this.props;
      loadSessionRequest(sessionId, success, failure);
    }
  }

  renderBackgroundAssemblyListener = () => {
    const { sessionId } = this.props;
    if (!sessionId) return null;

    return (
      <ActionCableConsumer
        channel={{ channel: 'BackgroundAssemblyChannel', app_session_id: this.props.sessionId }}
        onReceived={this.handleBackgroundAssemblyCompletion}
      />
    );
  }

  renderAppSessionListener = () => {
    const { sessionId } = this.props;
    if (!sessionId) return null;

    return (
      <ActionCableConsumer
        channel={{ channel: 'AppSessionChannel', app_session_id: this.props.sessionId }}
        onReceived={this.handleAppSessionTransmission}
        onConnected={this.handleAppSessionConnection}
      />
    );
  }

  renderSnapshotListener = () => {
    const { sessionId } = this.props;
    if (!sessionId) return null;

    return (
      <ActionCableConsumer
        channel={{ channel: 'SnapshotsChannel', app_session_id: this.props.sessionId }}
        onReceived={this.handleSnapshotChannelTransmission}
      />
    );
  }

  renderFastForwardProgressListener = () => {
    const { sessionId } = this.props;
    if (!sessionId) return null;

    return (
      <ActionCableConsumer
        channel={{ channel: 'FastForwardProgressChannel', app_session_id: this.props.sessionId }}
        onReceived={this.handleFastForwardProgressChannelTransmission}
      />
    );
  }

  render() {
    // We can add further listeners as they become necessary.
    return (
      <React.Fragment>
        {this.renderBackgroundAssemblyListener()}
        {this.renderAppSessionListener()}
        {this.renderSnapshotListener()}
        {this.renderFastForwardProgressListener()}
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state) => {
  // If these are not found, an empty map is returned. Otherwise they're strings.

  let sessionId = getSessionId(state);
  if (sessionId.size === 0) sessionId = null;

  const hasWebsocketConnection = getHasWebsocketConnection(state);

  return {
    sessionId,
    hasWebsocketConnection,
  };
};

const mapDispatchToProps = (dispatch) => ({
  updateAssembledDocuments: (data) => dispatch(updateAssembledDocuments(data)),
  loadNewState: (data) => dispatch(loadNewState(data)),
  updateUi: (data) => dispatch(updateUi(data)),
  appendToFastForwardProgress: (data) => dispatch(appendToFastForwardProgress(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ActionCableListener);
